/** @module app */

import { icons } from '@/icons/index'
import { createApp } from 'vue'
import i18n, { DEFAULT_LOCALE, loadMessages } from '@/i18n'
import { CIcon } from '@coreui/icons-vue'
import logger from '@/logger'
import { initBrandingStore } from '@/store'
import CoreUiChart from '@coreui/vue-chartjs'
import CoreUi from '@coreui/vue-pro'
import JsonExcel from 'vue-json-excel3'
import VueDOMPurifyHTML from 'vue-dompurify-html'

/**
 * Factory to create (and mount) a new VueJs (CoreUI-Vue) app.
 *
 * @function
 *
 * @param {object} params
 * @param {constructor} params.App has to be the Vue component, to be mounted.
 * @param {string} params.mountPoint (optional) is a CSS selector of the mount point. If empty, the App will not mounted yet, however, created.
 * @param {object} params.router (optional) should be a VueRouter
 * @param {object} params.store (optional) should be a Vuex.Store
 * @param {object} params.props (optional) is a VueJS props-object passed to the `App`
 *
 * @param {object} opts
 * @param {boolean} opts.waitForContentLoaded (optional, defaults to `true`) indicates to wait for event `DOMContentLoaded` before mounting. Note, if `true`, will return before mounting finished.
 *
 * @return {Vue} the app
 */
export async function mountApp(
  { App, mountPoint, router, store, props, locale },
  { onlyPublic = false, waitForContentLoaded = true } = {}
) {
  if (!App) {
    throw new TypeError('Failed to create Vue App. App is missing.')
  }
  logger.info('Creating/mounting the Vue App...')

  const dataAttrs = {}
  if (props) {
    dataAttrs.props = props
  }

  // see https://vuejs.org/api/application.html#createapp
  const app = createApp(App, props)

  app.config.performance = true

  app.use(logger)
  if (store) app.use(store)
  app.use(i18n)

  if (router) {
    app.use(router)
  }

  app.use(CoreUi)
  app.use(CoreUiChart)

  app.use(VueDOMPurifyHTML, {
    namedConfigurations: {
      svg: {
        USE_PROFILES: { svg: true }
      }
    }
  })

  app.provide('icons', icons)
  app.component('CIcon', CIcon)

  app.component('DownloadExcel', JsonExcel)

  logger.info('Created the App.')

  try {
    locale ||= DEFAULT_LOCALE
    if (store) {
      try {
        await store.dispatch('user/getUser')
        store.dispatch('user/trackDarkTheme')
        locale = store.getters['user/locale']
      } catch (err) {
        logger.error('Failed to extract current user.')
        logger.error(err)
      }
    }

    await loadMessages({ locale, onlyPublic })
    logger.info(`Loaded ${locale} translations.`)
  } catch (err) {
    logger.error('Failed to load translations.')
    logger.error(err)
  }

  const init = () => {
    app.mount(mountPoint)
    if (store) initBrandingStore(store)
  }

  waitForContentLoaded = waitForContentLoaded && document.readyState === 'loading'
  if (waitForContentLoaded && mountPoint) {
    document.addEventListener('DOMContentLoaded', () => {
      init()
      logger.info('DOM loaded. Mounted the App.')
    })
  } else if (mountPoint) {
    init()
    logger.info('Mounted the App.')
  }

  return app
}
